/**
 * Ops onboarding persistence (localStorage).
 *
 * Keys:
 * - ops.onboarding.isNew.{userId}.{orgId}
 * - ops.onboarding_state.{userId}.{orgId}
 * - ops.onboarding.laterDismissed.{userId}.{orgId} (sessionStorage)
 * - ops.onboarding.liveSession (sessionStorage)
 */

const IS_NEW_PREFIX = "ops.onboarding.isNew.";
const STATE_PREFIX = "ops.onboarding_state.";
const LATER_DISMISSED_PREFIX = "ops.onboarding.laterDismissed.";
const LIVE_SESSION_KEY = "ops.onboarding.liveSession";

/** Legacy Ops org-setup keys — purged when finishing or clearing. */
const LEGACY_ORG_SETUP_COMPLETED_PREFIX = "ops.setup.completed.";
const LEGACY_ORG_SETUP_PROMPTED_PREFIX = "ops.setup.prompted.";

const STEP_VALUES = [
  "materials",
  "sites",
  "containers",
  "pickup",
  "vehicles",
  "drivers",
] as const;

export type OnboardingPage = (typeof STEP_VALUES)[number];
export type OnboardingSpot = string;

export type OnboardingState = {
  page: OnboardingPage | null;
  spot: OnboardingSpot;
  completed: OnboardingPage[];
  resumePage?: OnboardingPage | null;
  resumeSpot?: OnboardingSpot;
};

const EMPTY_STATE: OnboardingState = {
  page: null,
  spot: "hub",
  completed: [],
  resumePage: null,
  resumeSpot: undefined,
};

function isNewKey(userId: string, organizationId: string): string {
  return `${IS_NEW_PREFIX}${userId}.${organizationId}`;
}

function stateKey(userId: string, organizationId: string): string {
  return `${STATE_PREFIX}${userId}.${organizationId}`;
}

function laterDismissedKey(userId: string, organizationId: string): string {
  return `${LATER_DISMISSED_PREFIX}${userId}.${organizationId}`;
}

function parsePage(raw: unknown): OnboardingPage | null {
  return typeof raw === "string" &&
    (STEP_VALUES as readonly string[]).includes(raw)
    ? (raw as OnboardingPage)
    : null;
}

function parseCompleted(raw: unknown): OnboardingPage[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map(parsePage)
    .filter((s): s is OnboardingPage => s != null);
}

function parseState(raw: string | null): OnboardingState | null {
  if (!raw) return null;
  try {
    const data = JSON.parse(raw) as Partial<OnboardingState>;
    return {
      page: parsePage(data.page),
      spot: typeof data.spot === "string" ? data.spot : "hub",
      completed: parseCompleted(data.completed),
      resumePage: parsePage(data.resumePage),
      resumeSpot:
        typeof data.resumeSpot === "string" ? data.resumeSpot : undefined,
    };
  } catch {
    return null;
  }
}

export function getOnboardingIsNew(
  userId: string,
  organizationId: string,
): boolean {
  const key = isNewKey(userId, organizationId);
  const raw = localStorage.getItem(key);
  if (raw === "true" || raw === "false") return raw === "true";
  localStorage.setItem(key, "true");
  if (!localStorage.getItem(stateKey(userId, organizationId))) {
    localStorage.setItem(
      stateKey(userId, organizationId),
      JSON.stringify(EMPTY_STATE),
    );
  }
  return true;
}

export function setOnboardingIsNew(
  userId: string,
  organizationId: string,
  value: boolean,
): void {
  localStorage.setItem(isNewKey(userId, organizationId), value ? "true" : "false");
}

export function getOnboardingState(
  userId: string,
  organizationId: string,
): OnboardingState {
  return (
    parseState(localStorage.getItem(stateKey(userId, organizationId))) ?? {
      ...EMPTY_STATE,
    }
  );
}

export function setOnboardingState(
  userId: string,
  organizationId: string,
  state: OnboardingState,
): void {
  localStorage.setItem(stateKey(userId, organizationId), JSON.stringify(state));
}

export function clearOnboardingState(
  userId: string,
  organizationId: string,
): void {
  localStorage.removeItem(stateKey(userId, organizationId));
}

export function patchOnboardingState(
  userId: string,
  organizationId: string,
  patch: Partial<OnboardingState>,
): OnboardingState {
  const next = { ...getOnboardingState(userId, organizationId), ...patch };
  setOnboardingState(userId, organizationId, next);
  return next;
}

export function finishOnboarding(
  userId: string,
  organizationId: string,
): void {
  setOnboardingIsNew(userId, organizationId, false);
  clearOnboardingState(userId, organizationId);
  clearOnboardingLaterDismissed(userId, organizationId);
  clearOnboardingLiveSession();
  localStorage.removeItem(`${LEGACY_ORG_SETUP_COMPLETED_PREFIX}${organizationId}`);
  sessionStorage.removeItem(
    `${LEGACY_ORG_SETUP_PROMPTED_PREFIX}${organizationId}`,
  );
}

export function markOnboardingLaterDismissed(
  userId: string,
  organizationId: string,
): void {
  sessionStorage.setItem(laterDismissedKey(userId, organizationId), "1");
}

export function isOnboardingLaterDismissed(
  userId: string,
  organizationId: string,
): boolean {
  return sessionStorage.getItem(laterDismissedKey(userId, organizationId)) === "1";
}

export function clearOnboardingLaterDismissed(
  userId: string,
  organizationId: string,
): void {
  sessionStorage.removeItem(laterDismissedKey(userId, organizationId));
}

export function clearAllOnboardingLaterDismissed(): void {
  const keys: string[] = [];
  for (let i = 0; i < sessionStorage.length; i += 1) {
    const key = sessionStorage.key(i);
    if (key?.startsWith(LATER_DISMISSED_PREFIX)) keys.push(key);
  }
  keys.forEach((key) => sessionStorage.removeItem(key));
}

export function markOnboardingLiveSession(): void {
  sessionStorage.setItem(LIVE_SESSION_KEY, "1");
}

export function isOnboardingLiveSession(): boolean {
  return sessionStorage.getItem(LIVE_SESSION_KEY) === "1";
}

export function clearOnboardingLiveSession(): void {
  sessionStorage.removeItem(LIVE_SESSION_KEY);
}

export function clearAllLegacySelfSetupKeys(): void {
  const keys: string[] = [];
  for (let i = 0; i < localStorage.length; i += 1) {
    const key = localStorage.key(i);
    if (
      key?.startsWith(LEGACY_ORG_SETUP_COMPLETED_PREFIX) ||
      key?.startsWith("ops.setup.")
    ) {
      keys.push(key);
    }
  }
  keys.forEach((key) => localStorage.removeItem(key));
}
